// Production-Ready Study Guide Viewer
// Features: Reading progress, bookmarks, print-friendly, note-taking, section navigation

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { 
  BookOpen, 
  ChevronDown, 
  ChevronRight, 
  Target, 
  Lightbulb, 
  CheckCircle,
  Bookmark,
  BookmarkCheck,
  Download,
  Printer,
  Copy,
  Clock,
  GraduationCap,
  StickyNote,
  CheckSquare,
  Square,
  FileText,
  FileImage,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { StudyGuide, StudyGuideSection } from '@/lib/content-studio/types';
import { useContentExporter } from '@/hooks/useContentExporter';

interface StudyGuideViewerProps {
  guide: StudyGuide;
  className?: string;
}

interface SectionNote {
  sectionIndex: number;
  note: string;
  timestamp: Date;
}

export function StudyGuideViewer({ guide, className = '' }: StudyGuideViewerProps) {
  const [openSections, setOpenSections] = useState<Set<number>>(new Set([0]));
  const [bookmarkedSections, setBookmarkedSections] = useState<Set<number>>(new Set());
  const [completedSections, setCompletedSections] = useState<Set<number>>(new Set());
  const [notes, setNotes] = useState<SectionNote[]>([]);
  const [activeNoteSection, setActiveNoteSection] = useState<number | null>(null);
  const [noteText, setNoteText] = useState('');
  const [completedObjectives, setCompletedObjectives] = useState<Set<number>>(new Set());
  const [readingStartTime] = useState<Date>(new Date());
  const [readTime, setReadTime] = useState(0);
  const sectionRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  
  // Export functionality
  const { exportToDocx, exportToPdf, isExporting } = useContentExporter();

  // Update reading time
  useEffect(() => {
    const interval = setInterval(() => {
      setReadTime(Math.floor((new Date().getTime() - readingStartTime.getTime()) / 60000));
    }, 60000);
    return () => clearInterval(interval);
  }, [readingStartTime]);

  // Calculate progress
  const progress = useMemo(() => {
    if (!guide?.sections?.length) return 0;
    return Math.round((completedSections.size / guide.sections.length) * 100);
  }, [completedSections, guide?.sections?.length]);

  const toggleSection = (index: number) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const toggleBookmark = (index: number) => {
    setBookmarkedSections(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
        toast.success('Bookmark removed');
      } else {
        next.add(index);
        toast.success('Section bookmarked');
      }
      return next;
    });
  };

  const toggleComplete = (index: number) => {
    setCompletedSections(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const toggleObjective = (index: number) => {
    setCompletedObjectives(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const saveNote = useCallback((sectionIndex: number) => {
    if (!noteText.trim()) return;
    
    setNotes(prev => [
      ...prev.filter(n => n.sectionIndex !== sectionIndex),
      { sectionIndex, note: noteText, timestamp: new Date() }
    ]);
    setNoteText('');
    setActiveNoteSection(null);
    toast.success('Note saved');
  }, [noteText]);

  const scrollToSection = (index: number) => {
    const el = sectionRefs.current.get(index);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setOpenSections(prev => new Set([...prev, index]));
    }
  };

  // Export as markdown
  const exportAsMarkdown = () => {
    let md = `# ${guide.title}\n\n`;
    md += `${guide.overview}\n\n`;
    
    if (guide.learningObjectives?.length) {
      md += `## Learning Objectives\n\n`;
      guide.learningObjectives.forEach((obj, i) => {
        md += `${i + 1}. ${obj}\n`;
      });
      md += '\n';
    }

    guide.sections?.forEach(section => {
      md += `## ${section.title}\n\n`;
      if (section.content) md += `${section.content}\n\n`;
      if (section.keyPoints?.length) {
        md += `### Key Points\n\n`;
        section.keyPoints.forEach(point => {
          md += `- ${point}\n`;
        });
        md += '\n';
      }
    });

    if (guide.glossary?.length) {
      md += `## Glossary\n\n`;
      guide.glossary.forEach(item => {
        md += `**${item.term}**: ${item.definition}\n\n`;
      });
    }

    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${guide.title.replace(/\s+/g, '-').toLowerCase()}-study-guide.md`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported as Markdown');
  };

  // Print-friendly version
  const printGuide = () => {
    window.print();
  };

  // Copy all content
  const copyContent = async () => {
    let text = `${guide.title}\n\n${guide.overview}\n\n`;
    
    guide.sections?.forEach(section => {
      text += `${section.title}\n`;
      if (section.content) text += `${section.content}\n`;
      section.keyPoints?.forEach(point => {
        text += `• ${point}\n`;
      });
      text += '\n';
    });

    await navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (!guide) {
    return (
      <div className={`flex items-center justify-center p-8 text-muted-foreground ${className}`}>
        <BookOpen className="w-8 h-8 mr-3 opacity-50" />
        <span>No study guide generated</span>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className={cn('flex flex-col', className)}>
        {/* Header with progress and actions */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold">{guide.title}</h2>
            <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {readTime || '<1'}m reading
              </span>
              <span className="flex items-center gap-1">
                <GraduationCap className="w-3 h-3" />
                {progress}% complete
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={isExporting}>
                  <Download className="h-4 w-4 mr-1" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-popover">
                <DropdownMenuItem onClick={() => exportToPdf('study-guide', guide)}>
                  <FileImage className="w-4 h-4 mr-2" />
                  PDF Document
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportToDocx('study-guide', guide)}>
                  <FileText className="w-4 h-4 mr-2" />
                  Word Document (.docx)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={exportAsMarkdown}>
                  <BookOpen className="w-4 h-4 mr-2" />
                  Markdown
                </DropdownMenuItem>
                <DropdownMenuItem onClick={copyContent}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Text
                </DropdownMenuItem>
                <DropdownMenuItem onClick={printGuide}>
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-4">
          <Progress value={progress} className="h-2" />
        </div>

        <div className="flex gap-4">
          {/* Main content */}
          <div className="flex-1 space-y-4">
            {/* Overview */}
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground">{guide.overview}</p>
              </CardContent>
            </Card>

            {/* Learning Objectives */}
            {guide.learningObjectives && guide.learningObjectives.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Target className="w-4 h-4 text-primary" />
                    Learning Objectives
                    <Badge variant="secondary" className="ml-auto text-xs">
                      {completedObjectives.size}/{guide.learningObjectives.length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <ul className="space-y-2">
                    {guide.learningObjectives.map((objective, idx) => (
                      <li 
                        key={idx} 
                        className={cn(
                          "flex items-start gap-2 text-sm cursor-pointer p-2 rounded-lg transition-colors",
                          "hover:bg-muted/50",
                          completedObjectives.has(idx) && "bg-green-50 dark:bg-green-950/30"
                        )}
                        onClick={() => toggleObjective(idx)}
                      >
                        {completedObjectives.has(idx) ? (
                          <CheckSquare className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        ) : (
                          <Square className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                        )}
                        <span className={completedObjectives.has(idx) ? 'line-through text-muted-foreground' : ''}>
                          {objective}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Key Facts */}
            {guide.quickReference?.keyFacts && guide.quickReference.keyFacts.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-amber-500" />
                    Key Facts
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex flex-wrap gap-2">
                    {guide.quickReference.keyFacts.map((fact, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs py-1">
                        {fact}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Sections */}
            {guide.sections && guide.sections.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  Study Sections
                </h3>
                {guide.sections.map((section, idx) => {
                  const isOpen = openSections.has(idx);
                  const isBookmarked = bookmarkedSections.has(idx);
                  const isCompleted = completedSections.has(idx);
                  const sectionNote = notes.find(n => n.sectionIndex === idx);

                  return (
                    <div
                      key={idx}
                      ref={(el) => {
                        if (el) sectionRefs.current.set(idx, el);
                      }}
                    >
                      <Collapsible open={isOpen} onOpenChange={() => toggleSection(idx)}>
                        <Card className={cn(
                          isCompleted && "border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/20"
                        )}>
                          <CollapsibleTrigger className="w-full">
                            <CardHeader className="py-3 hover:bg-muted/50 transition-colors">
                              <div className="flex items-center justify-between">
                                <CardTitle className="text-sm font-medium flex items-center gap-2">
                                  {isOpen ? (
                                    <ChevronDown className="w-4 h-4" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4" />
                                  )}
                                  {isCompleted && (
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                  )}
                                  {section.title}
                                </CardTitle>
                                <div className="flex items-center gap-2">
                                  {sectionNote && (
                                    <Badge variant="outline" className="text-xs">
                                      <StickyNote className="w-3 h-3 mr-1" />
                                      Note
                                    </Badge>
                                  )}
                                  <Badge variant="outline" className="text-xs">
                                    {section.keyPoints?.length || 0} key points
                                  </Badge>
                                </div>
                              </div>
                            </CardHeader>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <CardContent className="pt-0">
                              {/* Section actions */}
                              <div className="flex items-center gap-2 mb-3">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleBookmark(idx);
                                      }}
                                    >
                                      {isBookmarked ? (
                                        <BookmarkCheck className="w-4 h-4 text-primary" />
                                      ) : (
                                        <Bookmark className="w-4 h-4" />
                                      )}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {isBookmarked ? 'Remove bookmark' : 'Bookmark section'}
                                  </TooltipContent>
                                </Tooltip>
                                
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleComplete(idx);
                                      }}
                                    >
                                      {isCompleted ? (
                                        <CheckCircle className="w-4 h-4 text-green-600" />
                                      ) : (
                                        <CheckCircle className="w-4 h-4" />
                                      )}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {isCompleted ? 'Mark incomplete' : 'Mark complete'}
                                  </TooltipContent>
                                </Tooltip>

                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveNoteSection(activeNoteSection === idx ? null : idx);
                                        setNoteText(sectionNote?.note || '');
                                      }}
                                    >
                                      <StickyNote className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Add note</TooltipContent>
                                </Tooltip>
                              </div>

                              {/* Note input */}
                              {activeNoteSection === idx && (
                                <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-950/30 rounded-lg border border-yellow-200 dark:border-yellow-800">
                                  <Textarea
                                    placeholder="Add your notes here..."
                                    value={noteText}
                                    onChange={(e) => setNoteText(e.target.value)}
                                    className="min-h-[80px] mb-2 bg-background"
                                  />
                                  <div className="flex justify-end gap-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setActiveNoteSection(null)}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() => saveNote(idx)}
                                    >
                                      Save Note
                                    </Button>
                                  </div>
                                </div>
                              )}

                              {/* Existing note display */}
                              {sectionNote && activeNoteSection !== idx && (
                                <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-950/30 rounded-lg border border-yellow-200 dark:border-yellow-800">
                                  <p className="text-sm">{sectionNote.note}</p>
                                  <p className="text-xs text-muted-foreground mt-2">
                                    Noted on {sectionNote.timestamp.toLocaleDateString()}
                                  </p>
                                </div>
                              )}

                              {section.content && (
                                <p className="text-sm text-muted-foreground mb-4">
                                  {section.content}
                                </p>
                              )}
                              
                              {section.keyPoints && section.keyPoints.length > 0 && (
                                <div className="space-y-2">
                                  <p className="text-xs font-medium text-muted-foreground">Key Points:</p>
                                  <ul className="space-y-1.5">
                                    {section.keyPoints.map((point, pointIdx) => (
                                      <li key={pointIdx} className="flex items-start gap-2 text-sm">
                                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                                        <span>{point}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {section.examples && section.examples.length > 0 && (
                                <div className="mt-4 p-3 bg-muted rounded-lg">
                                  <p className="text-xs font-medium text-muted-foreground mb-2">Examples:</p>
                                  <ul className="space-y-1">
                                    {section.examples.map((example, exIdx) => (
                                      <li key={exIdx} className="text-sm italic">
                                        "{example}"
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {section.practiceQuestions && section.practiceQuestions.length > 0 && (
                                <div className="mt-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                                  <p className="text-xs font-medium text-muted-foreground mb-2">Practice Questions:</p>
                                  <ol className="space-y-1 list-decimal list-inside">
                                    {section.practiceQuestions.map((question, qIdx) => (
                                      <li key={qIdx} className="text-sm">
                                        {question}
                                      </li>
                                    ))}
                                  </ol>
                                </div>
                              )}
                            </CardContent>
                          </CollapsibleContent>
                        </Card>
                      </Collapsible>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Glossary */}
            {guide.glossary && guide.glossary.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Glossary</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <dl className="space-y-2">
                    {guide.glossary.map((item, idx) => (
                      <div key={idx} className="border-b border-border pb-2 last:border-0">
                        <dt className="font-medium text-sm">{item.term}</dt>
                        <dd className="text-sm text-muted-foreground">{item.definition}</dd>
                      </div>
                    ))}
                  </dl>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar: Table of Contents */}
          <div className="hidden lg:block w-48 flex-shrink-0">
            <Card className="sticky top-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium">Contents</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <nav className="space-y-1">
                  {guide.sections?.map((section, idx) => (
                    <button
                      key={idx}
                      onClick={() => scrollToSection(idx)}
                      className={cn(
                        "w-full text-left px-2 py-1 text-xs rounded transition-colors",
                        "hover:bg-muted/50 truncate flex items-center gap-1",
                        openSections.has(idx) && "text-primary font-medium",
                        completedSections.has(idx) && "text-green-600"
                      )}
                    >
                      {completedSections.has(idx) && (
                        <CheckCircle className="w-3 h-3 flex-shrink-0" />
                      )}
                      {bookmarkedSections.has(idx) && (
                        <Bookmark className="w-3 h-3 flex-shrink-0 text-primary" />
                      )}
                      <span className="truncate">{section.title}</span>
                    </button>
                  ))}
                </nav>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}

export default StudyGuideViewer;
